import { useState, useRef } from 'react';
import { Stack, Group, Button, Image, Text, SimpleGrid, ActionIcon, Paper, Badge } from '../../../components/gab/GabComponents';
import { extractPhotoMetadata, type PhotoMetadata } from '../lib/exif';

interface Props {
  photos?: File[];
  onChange: (photos: File[]) => void;
  onGpsExtracted?: (gps: { lat: number; lng: number }) => void;
  onExifMetadata?: (metadata: PhotoMetadata) => void;
  maxPhotos?: number;
}

export default function PhotoCapture({ photos = [], onChange, onGpsExtracted, onExifMetadata, maxPhotos = 10 }: Props) {
  const cameraRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLInputElement>(null);
  const [previews, setPreviews] = useState<string[]>([]);
  const [gpsFound, setGpsFound] = useState(false);
  const [dragging, setDragging] = useState(false);

  const handleFiles = async (files: File[]) => {
    const newPhotos = [...photos];
    const newPreviews = [...previews];

    for (const file of files) {
      if (newPhotos.length >= maxPhotos) break;
      newPhotos.push(file);
      newPreviews.push(URL.createObjectURL(file));
    }

    setPreviews(newPreviews);
    onChange(newPhotos);

    for (const file of files) {
      try {
        const metadata = await extractPhotoMetadata(file);
        if (metadata?.gps) {
          setGpsFound(true);
          if (onGpsExtracted) onGpsExtracted(metadata.gps);
          if (onExifMetadata) onExifMetadata(metadata);
          break;
        }
      } catch {
        // EXIF extraction failed
      }
    }
  };

  const handleCamera = () => {
    cameraRef.current?.click();
  };

  const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length) handleFiles(files);
  };

  const removePhoto = (index: number) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    URL.revokeObjectURL(previews[index]);
    setPreviews(newPreviews);
    onChange(newPhotos);
  };

  return (
    <Stack gap="sm">
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleCameraCapture}
      />

      <Group gap="sm">
        <Button
          leftSection={<i className="fa-solid fa-camera" />}
          onClick={handleCamera}
          variant="filled"
          size="md"
        >
          Take Photo
        </Button>
        <Text size="xs" c="dimmed">{photos.length}/{maxPhotos} photos</Text>
        {gpsFound && (
          <Badge size="xs" variant="light" color="var(--color-success)" leftSection={<span style={{ fontSize: 12 }}>📍</span>}>
            Location extracted from photo
          </Badge>
        )}
      </Group>

      <input
        ref={dropRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={e => { const files = Array.from(e.target.files || []); if (files.length) handleFiles(files); e.target.value = ''; }}
      />
      <div
        onClick={() => photos.length < maxPhotos && dropRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => {
          e.preventDefault();
          setDragging(false);
          if (photos.length >= maxPhotos) return;
          const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
          if (files.length) handleFiles(files);
        }}
        style={{
          minHeight: 80,
          border: `2px dashed ${dragging ? 'var(--color-primary-700)' : 'var(--color-neutral-300)'}`,
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '0.5rem',
          cursor: photos.length >= maxPhotos ? 'not-allowed' : 'pointer',
          background: dragging ? 'var(--color-primary-50)' : 'transparent',
          opacity: photos.length >= maxPhotos ? 0.5 : 1,
          transition: 'border-color 0.15s, background 0.15s',
        }}
      >
        <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: 20, opacity: 0.5 }} />
        <Text size="sm" c="dimmed">Drop photos here or click to browse</Text>
      </div>

      {previews.length > 0 && (
        <SimpleGrid cols={{ base: 3, sm: 4, md: 5 }} spacing="xs">
          {previews.map((src, i) => (
            <Paper key={i} pos="relative" withBorder radius="sm" style={{ overflow: 'hidden' }}>
              <Image src={src} h={80} fit="cover" />
              <ActionIcon
                size="xs"
                color="var(--color-error)"
                variant="filled"
                pos="absolute"
                top={4}
                right={4}
                onClick={() => removePhoto(i)}
              >
                <i className="fa-solid fa-xmark" style={{ fontSize: 12 }} />
              </ActionIcon>
            </Paper>
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}
